import {useQuery} from '@apollo/client';
import * as React from 'react';
import {useNavigate, useParams} from 'react-router';

import {QUERY_STUDENT_WOD} from '../studentQueries';
import {StudentTheme} from '../studentTheme';
import {ExerciseWorkout, exerciseSummary} from '../wodsDay';

interface Workout {
  id: string;
  title?: string | null;
  description?: string | null;
  timeText?: string | null;
  exerciseWorkouts?: ExerciseWorkout[] | null;
}

interface WodDetail {
  id: string;
  name: string;
  description?: string | null;
  completedOnDate?: boolean | null;
  cover?: {uri?: string | null} | null;
  video?: {uri?: string | null} | null;
  workouts?: Workout[] | null;
}

interface WodData {
  wod?: WodDetail | null;
}

interface Props {
  theme: StudentTheme;
}

export const WodDetailScreen: React.FC<Props> = ({theme}) => {
  const {wodId} = useParams();
  const navigate = useNavigate();

  const {data, loading, error, refetch} = useQuery<WodData>(QUERY_STUDENT_WOD, {
    variables: {id: wodId},
    skip: !wodId,
  });

  const wod = data?.wod;
  const workouts = wod?.workouts ?? [];

  return (
    <div className="wod-detalhe">
      <button
        type="button"
        className="wod-detalhe__voltar student-app__retry"
        style={{color: theme.primary}}
        onClick={() => navigate(-1)}
      >
        <i className="fa-light fa-chevron-left" aria-hidden="true" /> Voltar
      </button>

      {loading && <p className="student-screen__estado">Carregando…</p>}

      {error && !loading && (
        <div className="student-screen__estado">
          <p>Não foi possível carregar o treino.</p>
          <button type="button" className="student-app__retry" onClick={() => refetch()}>
            Tentar de novo
          </button>
        </div>
      )}

      {!loading && !error && !wod && <p className="student-screen__estado">Treino não encontrado.</p>}

      {!loading && !error && wod && (
        <article className="wod-detalhe__conteudo" style={{backgroundColor: theme.body, borderColor: theme.border}}>
          {wod.cover?.uri && <img className="wod-card__capa" src={wod.cover.uri} alt="" loading="lazy" />}

          <header className="wod-detalhe__topo">
            <h2 className="wod-card__nome">{wod.name}</h2>
            {wod.completedOnDate && (
              <i
                className="fa-solid fa-circle-check wod-card__feito"
                style={{color: theme.primary}}
                aria-label="Concluído"
              />
            )}
          </header>

          {wod.description && <p className="wod-card__descricao">{wod.description}</p>}

          {wod.video?.uri && (
            <a
              className="wod-detalhe__video"
              style={{color: theme.primary}}
              href={wod.video.uri}
              target="_blank"
              rel="noreferrer"
            >
              <i className="fa-light fa-circle-play" aria-hidden="true" /> Assistir ao vídeo
            </a>
          )}

          {workouts.length === 0 && (
            <p className="student-screen__estado" style={{color: theme.neutral}}>
              Este treino ainda não tem blocos cadastrados.
            </p>
          )}

          {workouts.map((workout) => (
            <section key={workout.id} className="wod-bloco" style={{borderColor: theme.border}}>
              <header className="wod-bloco__topo">
                <h3 className="wod-bloco__nome">{workout.title || 'Bloco'}</h3>
                {workout.timeText && (
                  <span className="wod-bloco__tempo" style={{color: theme.neutral}}>
                    {workout.timeText}
                  </span>
                )}
              </header>

              {workout.description && <p className="wod-bloco__descricao">{workout.description}</p>}

              {(workout.exerciseWorkouts ?? []).length > 0 && (
                <ul className="wod-bloco__exercicios">
                  {(workout.exerciseWorkouts ?? []).map((item) => (
                    <li key={item.id} className="wod-exercicio">
                      <span className="wod-exercicio__nome">{item.exercise?.name || 'Exercício'}</span>
                      {exerciseSummary(item) && (
                        <span className="wod-exercicio__detalhe" style={{color: theme.neutral}}>
                          {exerciseSummary(item)}
                        </span>
                      )}
                      {item.notes && (
                        <span className="wod-exercicio__nota" style={{color: theme.neutral}}>
                          {item.notes}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </article>
      )}
    </div>
  );
};
